import { create } from 'zustand';
import {
  getAssignments,
  createAssignment,
  deleteAssignment,
  addProblemsBulk,
  deleteProblem,
} from '../services/assignmentService';
import { ctGetAssignmentById, ctGetProblemsByAssignment } from '../services/api';

const useAssignmentStore = create((set, get) => ({
  assignments: [],
  studentCount: 0,          // ← new: batch student count from the list response
  currentAssignment: null,
  problems: [],
  loading: false,
  error: null,
  deleting: null,
  adding: false,
  _fetchedBatchId: null,
  _fetchedAssignmentId: null,
  _fetchedProblemsForId: null,

  fetchAssignments: async (batchId, force = false) => {
    if (!force && get()._fetchedBatchId === batchId && get().assignments.length > 0) return;
    set({ loading: true, error: null });
    try {
      const { data } = await getAssignments(batchId);
      set({
        assignments: data.assignments || [],
        studentCount: data.studentCount ?? 0,   // ← store it
        _fetchedBatchId: batchId,
      });
    } catch (e) {
      set({ error: e?.response?.data?.message || e.message });
    } finally {
      set({ loading: false });
    }
  },

  fetchAssignmentById: async (assignmentId, force = false) => {
    if (!force && get()._fetchedAssignmentId === assignmentId && get().currentAssignment) return;
    set({ loading: true, error: null });
    try {
      const { data } = await ctGetAssignmentById(assignmentId);
      set({ currentAssignment: data.assignment, _fetchedAssignmentId: assignmentId });
      return data.assignment;
    } catch (e) {
      set({ error: e?.response?.data?.message || e.message });
    } finally {
      set({ loading: false });
    }
  },

  addAssignment: async (batchId, form) => {
    const { data } = await createAssignment(batchId, form);
    set((s) => ({ assignments: [...s.assignments, data.assignment] }));
    return data.assignment;
  },

  removeAssignment: async (batchId, assignmentId) => {
    set({ deleting: assignmentId });
    try {
      await deleteAssignment(batchId, assignmentId);
      set((s) => ({
        assignments: s.assignments.filter((a) => a._id !== assignmentId),
        currentAssignment: s.currentAssignment?._id === assignmentId ? null : s.currentAssignment,
        _fetchedAssignmentId: s._fetchedAssignmentId === assignmentId ? null : s._fetchedAssignmentId,
      }));
    } finally {
      set({ deleting: null });
    }
  },

  fetchProblems: async (assignmentId, force = false) => {
    if (!force && get()._fetchedProblemsForId === assignmentId && get().problems.length > 0) return;
    set({ loading: true, error: null });
    try {
      const { data } = await ctGetProblemsByAssignment(assignmentId);
      set({ problems: data.problems || [], _fetchedProblemsForId: assignmentId });
    } catch (e) {
      set({ error: e?.response?.data?.message || e.message });
    } finally {
      set({ loading: false });
    }
  },

  bulkAddProblems: async (assignmentId, links) => {
    set({ adding: true });
    try {
      const { data } = await addProblemsBulk(assignmentId, links);
      // Refetch problems after bulk add
      const res = await ctGetProblemsByAssignment(assignmentId);
      set({ problems: res.data.problems || [], _fetchedProblemsForId: assignmentId });
      return data;
    } finally {
      set({ adding: false });
    }
  },

  removeProblem: async (assignmentId, problemId) => {
    set({ deleting: problemId });
    try {
      await deleteProblem(assignmentId, problemId);
      set((s) => ({ problems: s.problems.filter((p) => p._id !== problemId) }));
    } finally {
      set({ deleting: null });
    }
  },
}));

export default useAssignmentStore;
